import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import PreferencesDataTable from '../../../../../components/common/PreferencesDataTable';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { PRIMARY } from '../../../components/LmsSectionCard';
import { NEWS_STATUS, NEWS_STATUS_I18N_KEY, NEWS_STATUS_SEVERITY, NEWS_TYPE, formatLocalDate, isAnnouncement } from '../../utils/newsUtils';

const LmsNewsTable = ({ items, totalCount, loading, lazyParams, onPage, sortField, sortOrder, onSort, filters, onFilter, onClearFilters, onEdit, onDelete }) => {
    const { th } = useLanguage();

    const columns = useMemo(() => {
        const newsTypeOptions = [
            { label: th('lmsNews.newsTypes.training'), value: NEWS_TYPE.TRAINING },
            { label: th('lmsNews.newsTypes.announcement'), value: NEWS_TYPE.ANNOUNCEMENT }
        ];

        const statusOptions = Object.values(NEWS_STATUS).map((value) => ({ value, label: th(`lmsNews.statuses.${NEWS_STATUS_I18N_KEY[value]}`) }));

        const dateFilterElement = (options) => (
            <Calendar
                value={options.value}
                onChange={(e) => options.filterApplyCallback(e.value instanceof Date ? new Date(e.value.getFullYear(), e.value.getMonth(), e.value.getDate()) : null)}
                dateFormat="dd.mm.yy"
                placeholder={th('common.search')}
                showButtonBar
                appendTo={document.body}
                className="p-column-filter w-full"
                inputStyle={{ width: '100%' }}
            />
        );

        return [
            {
                field: '__actions',
                header: '',
                lockedFromPrefs: true,
                lockedPosition: 'start',
                style: { width: '6rem', textAlign: 'center' },
                filter: true,
                showFilterMenu: false,
                filterElement: () => (
                    <div className="flex justify-content-center">
                        <Button type="button" icon="pi pi-filter-slash" text rounded severity="secondary" aria-label={th('filter.reset')} tooltip={th('filter.reset')} tooltipOptions={{ position: 'top' }} onClick={onClearFilters} />
                    </div>
                ),
                body: (row) => (
                    <div className="flex justify-content-center">
                        <Button type="button" icon="pi pi-pencil" text rounded aria-label={th('common.edit')} onClick={() => onEdit(row)} />
                        {onDelete && <Button type="button" icon="pi pi-trash" text rounded severity="danger" aria-label={th('common.delete')} onClick={() => onDelete(row)} />}
                    </div>
                )
            },
            {
                field: 'title',
                header: th('lmsNews.columns.title'),
                sortable: true,
                filter: true,
                filterPlaceholder: th('lmsNews.columns.title'),
                style: { minWidth: '16rem' },
                body: (row) => (
                    <Link to={`/learning-management-system/news/${row.id}`} className="no-underline font-medium" style={{ color: PRIMARY }}>
                        {row.title || row.trainingName || '-'}
                    </Link>
                )
            },
            {
                field: 'newsType',
                header: th('lmsNews.columns.newsType'),
                sortable: true,
                filter: true,
                showFilterMenu: false,
                style: { width: '10rem' },
                body: (row) => <Tag value={isAnnouncement(row) ? th('lmsNews.newsTypes.announcement') : th('lmsNews.newsTypes.training')} severity={isAnnouncement(row) ? 'warning' : 'info'} />,
                filterElement: (options) => (
                    <Dropdown value={options.value} options={newsTypeOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('lmsNews.allNewsTypes')} className="p-column-filter w-full" showClear />
                )
            },
            { field: 'trainingName', header: th('lmsNews.columns.training'), body: (row) => row.trainingName || '-', sortable: true, filter: true, filterPlaceholder: th('lmsNews.columns.training'), style: { minWidth: '12rem' } },
            { field: 'planningLocationText', header: th('lmsNews.columns.location'), body: (row) => row.planningLocationText || '-', sortable: true, filter: true, filterPlaceholder: th('lmsNews.columns.location') },
            { field: 'publishDate', header: th('lmsNews.columns.publishDate'), body: (row) => formatLocalDate(row.publishDate), sortable: true, filter: true, showFilterMenu: false, filterElement: dateFilterElement, style: { width: '10rem', whiteSpace: 'nowrap' } },
            {
                field: 'status',
                header: th('lmsNews.columns.status'),
                style: { width: '1%', whiteSpace: 'nowrap' },
                sortable: true,
                filter: true,
                showFilterMenu: false,
                body: (row) => (row.status ? <Tag value={row.statusText || th(`lmsNews.statuses.${NEWS_STATUS_I18N_KEY[row.status]}`)} severity={NEWS_STATUS_SEVERITY[row.status]} className="white-space-nowrap" /> : '-'),
                filterElement: (options) => (
                    <MultiSelect
                        value={options.value}
                        options={statusOptions}
                        onChange={(e) => options.filterApplyCallback(e.value?.length ? e.value : null)}
                        placeholder={th('lmsNews.allStatuses')}
                        maxSelectedLabels={1}
                        appendTo={document.body}
                        className="p-column-filter w-full"
                        style={{ minWidth: '10rem' }}
                    />
                )
            },
            { field: 'createdName', header: th('lmsNews.columns.createdBy'), body: (row) => row.createdName || '-', sortable: true, filter: true, filterPlaceholder: th('lmsNews.columns.createdBy') },
            { field: 'createdDate', header: th('lmsNews.columns.createdDate'), body: (row) => formatLocalDate(row.createdDate), sortable: true, filter: true, showFilterMenu: false, filterElement: dateFilterElement, style: { width: '10rem', whiteSpace: 'nowrap' } }
        ];
    }, [onEdit, onDelete, onClearFilters, th]);

    return (
        <PreferencesDataTable
            tableKey="lms-news-management"
            columns={columns}
            value={items}
            loading={loading}
            dataKey="id"
            showGridlines
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows}
            totalRecords={totalCount}
            onPage={onPage}
            rowsPerPageOptions={[10, 20, 50]}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            filterDisplay="row"
            filters={filters}
            onFilter={onFilter}
            filterDelay={400}
            emptyMessage={th('lmsNews.emptyMessage')}
        />
    );
};

export default LmsNewsTable;
